import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, useScroll } from "framer-motion";
import { BRAND, NAV_PRIMARY } from "@/data/content";
import { EASE } from "@/components/Reveal";

const Dropdown = ({ item, pathname, go }) => {
  const [open, setOpen] = useState(false);
  const isActive = item.children.some((c) => pathname.startsWith(c.path.split("/").slice(0, 2).join("/")) && pathname !== "/");
  return (
    <div
      className="relative shrink-0"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        data-testid={`nav-dropdown-${item.label.toLowerCase()}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className={`group relative flex items-center gap-1.5 whitespace-nowrap py-1 text-[13px] font-medium uppercase tracking-[0.12em] transition-colors duration-500 hover:text-white 2xl:tracking-[0.16em] ${
          isActive ? "text-white" : "text-slate-300"
        }`}
      >
        {item.label}
        <svg
          width="9"
          height="6"
          viewBox="0 0 9 6"
          fill="none"
          className={`shrink-0 transition-transform duration-500 ${open ? "rotate-180" : ""}`}
        >
          <path d="M1 1l3.5 3.5L8 1" stroke="currentColor" strokeWidth="1.2" />
        </svg>
        <span
          className={`absolute bottom-0 left-0 h-px w-full origin-left bg-gradient-to-r from-[#D4AF37]/80 to-transparent transition-transform duration-500 group-hover:scale-x-100 ${
            isActive ? "scale-x-100" : "scale-x-0"
          }`}
          aria-hidden="true"
        />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`nav-dropdown-panel-${item.label.toLowerCase()}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="absolute left-0 top-full z-50 mt-4 w-72 border border-white/10 bg-[#12100A]/95 py-3 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.6)] backdrop-blur-xl"
          >
            {item.children.map((c) => (
              <button
                key={c.path}
                data-testid={`nav-sub-link-${c.path.replace(/\//g, "-")}`}
                onClick={() => {
                  setOpen(false);
                  go(c.path);
                }}
                className="group flex w-full items-baseline gap-4 whitespace-nowrap px-6 py-3 text-left transition-colors duration-300 hover:bg-white/5"
              >
                {c.num && (
                  <span className="text-[10px] font-medium tracking-[0.2em] text-[#E7CE8B]">
                    {c.num}
                  </span>
                )}
                <span
                  className={`text-sm font-normal transition-colors duration-300 ${
                    pathname === c.path
                      ? "text-[#E7CE8B]"
                      : "text-slate-300 group-hover:text-white"
                  }`}
                >
                  {c.label}
                </span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const EnquiryButton = ({ onClick, className = "", testid = "nav-corporate-enquiry-btn" }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`shrink-0 whitespace-nowrap rounded-full border border-white/20 px-6 py-3 text-[13px] font-medium uppercase tracking-[0.14em] text-slate-100 transition-colors duration-500 hover:border-[#D4AF37]/60 hover:text-[#E7CE8B] 2xl:px-7 ${className}`}
  >
    Corporate Enquiries
  </button>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const menuScrollRef = useRef(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const solid = scrolled || pathname !== "/";
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    /* focus the menu's own scroll container so wheel/trackpad/keyboard
       input scrolls the menu (Lenis ignores it via data-lenis-prevent) */
    if (open) menuScrollRef.current?.focus({ preventScroll: true });
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const go = (path) => {
    setOpen(false);
    setMobileGroup(null);
    navigate(path);
  };

  const isChildActive = (item) =>
    item.children?.some(
      (c) => pathname.startsWith(c.path.split("/").slice(0, 2).join("/")) && pathname !== "/"
    );

  return (
    <>
      <motion.header
        data-testid="main-navbar"
        className={`fixed inset-x-0 top-0 z-40 w-full transition-[background-color,border-color,backdrop-filter] duration-700 ${
          solid
            ? "border-b border-white/10 bg-[#0A0906]/85 backdrop-blur-xl"
            : "border-b border-transparent bg-transparent"
        }`}
        initial={{ y: -90, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: EASE, delay: 0.1 }}
      >
        <div className="mx-auto flex h-[76px] w-full max-w-[1800px] items-center justify-between px-6 md:h-[84px] min-[1400px]:h-[96px] min-[1400px]:px-8 2xl:px-14">
          {/* LEFT — brand */}
          <button
            data-testid="nav-logo-link"
            onClick={() => go("/")}
            className="flex shrink-0 items-center gap-3.5 whitespace-nowrap focus:outline focus:outline-1 focus:outline-offset-4 focus:outline-slate-500"
            aria-label="Logibyte Technologies — home"
          >
            <img
              src={BRAND.logo}
              alt="Logibyte Technologies logo"
              className="h-9 w-9 rounded-full min-[1400px]:h-11 min-[1400px]:w-11"
            />
            <span className="text-[13px] font-medium uppercase tracking-[0.18em] text-slate-200 min-[1400px]:tracking-[0.22em]">
              Logibyte Technologies
            </span>
          </button>

          {/* CENTER — full navigation (wide desktops only) */}
          <nav
            className="hidden items-center gap-5 whitespace-nowrap min-[1400px]:flex 2xl:gap-8"
            aria-label="Primary"
          >
            {NAV_PRIMARY.map((item) =>
              item.children ? (
                <Dropdown key={item.label} item={item} pathname={pathname} go={go} />
              ) : (
                <button
                  key={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(item.path)}
                  className={`group relative shrink-0 whitespace-nowrap py-1 text-[13px] font-medium uppercase tracking-[0.12em] transition-colors duration-500 hover:text-white 2xl:tracking-[0.16em] ${
                    pathname === item.path ? "text-white" : "text-slate-300"
                  }`}
                >
                  {item.label}
                  <span
                    className={`absolute bottom-0 left-0 h-px w-full origin-left bg-gradient-to-r from-[#D4AF37]/80 to-transparent transition-transform duration-500 group-hover:scale-x-100 ${
                      pathname === item.path ? "scale-x-100" : "scale-x-0"
                    }`}
                    aria-hidden="true"
                  />
                </button>
              )
            )}
          </nav>

          {/* RIGHT — CTA (wide desktop) / CTA + menu (compact) */}
          <div className="flex shrink-0 items-center gap-4">
            <EnquiryButton
              onClick={() => go("/contact#enquiry")}
              className="hidden min-[1400px]:inline-block"
            />
            <EnquiryButton
              onClick={() => go("/contact#enquiry")}
              className="hidden sm:inline-block min-[1400px]:hidden"
              testid="nav-corporate-enquiry-compact-btn"
            />
            <button
              data-testid="mobile-menu-toggle"
              className="flex h-10 w-10 shrink-0 flex-col items-center justify-center gap-1.5 min-[1400px]:hidden"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <span className="h-px w-6 bg-slate-200" />
              <span className="h-px w-6 bg-slate-200" />
            </button>
          </div>
        </div>
        <motion.div
          className="absolute bottom-0 left-0 h-px w-full origin-left bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent"
          style={{ scaleX: scrollYProgress }}
          aria-hidden="true"
        />
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-50 flex h-[100dvh] flex-col bg-[#090704]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex shrink-0 items-center justify-between px-6 py-4">
              <div className="flex items-center gap-3">
                <img src={BRAND.logo} alt="" className="h-8 w-8 rounded-full" />
                <span className="text-xs font-medium uppercase tracking-[0.25em] text-slate-300">
                  Logibyte
                </span>
              </div>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="flex h-10 w-10 items-center justify-center text-slate-300"
                aria-label="Close menu"
              >
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M1 1l16 16M17 1L1 17" stroke="currentColor" strokeWidth="1" />
                </svg>
              </button>
            </div>
            <div
              ref={menuScrollRef}
              data-lenis-prevent
              tabIndex={-1}
              data-testid="mobile-menu-scroll"
              className="flex min-h-0 flex-1 flex-col overflow-y-auto overflow-x-hidden overscroll-contain focus:outline-none [-webkit-overflow-scrolling:touch]"
            >
            <nav className="flex flex-1 flex-col px-8 pb-8 pt-6" aria-label="Mobile">
              <motion.button
                data-testid="mobile-nav-link-home"
                className={`flex items-center gap-3 border-b border-white/5 py-4 text-left font-serif text-2xl tracking-tight transition-colors duration-300 ${
                  pathname === "/" ? "text-[#E7CE8B]" : "text-slate-200"
                }`}
                onClick={() => go("/")}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, ease: EASE }}
              >
                {pathname === "/" && (
                  <span className="h-1 w-1 rotate-45 bg-[#E7CE8B]" aria-hidden="true" />
                )}
                Home
              </motion.button>
              {NAV_PRIMARY.map((item, i) =>
                item.children ? (
                  <div key={item.label} className="border-b border-white/5">
                    <button
                      data-testid={`mobile-nav-group-${item.label.toLowerCase()}`}
                      className={`flex w-full items-center justify-between py-4 text-left font-serif text-2xl tracking-tight transition-colors duration-300 ${
                        isChildActive(item) ? "text-[#E7CE8B]" : "text-slate-200"
                      }`}
                      onClick={() => setMobileGroup(mobileGroup === item.label ? null : item.label)}
                      aria-expanded={mobileGroup === item.label}
                    >
                      <span className="flex items-center gap-3">
                        {isChildActive(item) && (
                          <span className="h-1 w-1 rotate-45 bg-[#E7CE8B]" aria-hidden="true" />
                        )}
                        {item.label}
                      </span>
                      <svg
                        width="12"
                        height="8"
                        viewBox="0 0 9 6"
                        fill="none"
                        className={`transition-transform duration-500 ${mobileGroup === item.label ? "rotate-180" : ""}`}
                      >
                        <path d="M1 1l3.5 3.5L8 1" stroke="currentColor" strokeWidth="1" />
                      </svg>
                    </button>
                    <AnimatePresence initial={false}>
                      {mobileGroup === item.label && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.4, ease: EASE }}
                          className="overflow-hidden"
                        >
                          <div className="pb-4">
                            {item.children.map((c) => (
                              <button
                                key={c.path}
                                data-testid={`mobile-nav-sub-${c.path.replace(/\//g, "-")}`}
                                onClick={() => go(c.path)}
                                className={`block w-full py-2.5 pl-4 text-left text-base font-normal transition-colors duration-300 ${
                                  pathname === c.path
                                    ? "text-[#E7CE8B]"
                                    : "text-slate-300 hover:text-white"
                                }`}
                              >
                                {c.label}
                              </button>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.button
                    key={item.path}
                    data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`flex items-center gap-3 border-b border-white/5 py-4 text-left font-serif text-2xl tracking-tight transition-colors duration-300 ${
                      pathname === item.path ? "text-[#E7CE8B]" : "text-slate-200"
                    }`}
                    onClick={() => go(item.path)}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, ease: EASE, delay: 0.04 * i }}
                  >
                    {pathname === item.path && (
                      <span className="h-1 w-1 rotate-45 bg-[#E7CE8B]" aria-hidden="true" />
                    )}
                    {item.label}
                  </motion.button>
                )
              )}
              <button
                data-testid="mobile-nav-enquiry-btn"
                onClick={() => go("/contact#enquiry")}
                className="mt-8 rounded-full border border-[#D4AF37]/50 px-8 py-3.5 text-xs font-medium uppercase tracking-[0.22em] text-[#E7CE8B]"
              >
                Corporate Enquiries
              </button>
            </nav>
            <p className="px-8 pb-10 text-xs uppercase tracking-[0.25em] text-slate-400">
              {BRAND.tagline}
            </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
